import React, { useState, useEffect, useMemo } from "react";
import { GetReleaseVersions, GetReleaseVersionIssues } from "../global/Apis";
import { useMsal, useAccount } from "@azure/msal-react";
import PreviewPlanModal from "./modals/PreviewPlanModal";
import { Listbox } from "@headlessui/react";
import { ReleaseVersion, ReleaseVersionIssue, FixVersionSummaryByCategory } from "../global/Types";
import ReleaseVersionsTable from "./ui/ReleaseVersionsTable";
import ReleaseVersionDetails from "./modals/ReleaseVersionDetails";
import ReleaseVersionsSummaryBar from "./ui/ReleaseVersionsSummaryBar";

const STATUS_COLORS: Record<string, string> = {
  "Ready for Development": "bg-gray-100 text-gray-600 border-gray-300",
  "Design Review":         "bg-gray-100 text-gray-600 border-gray-300",
  "In Progress":           "bg-blue-100 text-blue-700 border-blue-300",
  "Dev Complete":          "bg-indigo-100 text-indigo-700 border-indigo-300",
  "Staging & QA Testing":  "bg-purple-100 text-purple-700 border-purple-300",
  "Staging testing":       "bg-purple-100 text-purple-700 border-purple-300",
  "Ready for Staging":     "bg-emerald-100 text-emerald-800 border-emerald-300",
  "Ready for Production":  "bg-emerald-200 text-emerald-900 border-emerald-400",
  "Recently Closed":       "bg-emerald-200 text-emerald-900 border-emerald-400",
  "Closed":                "bg-emerald-200 text-emerald-900 border-emerald-400",
  "Reopened":              "bg-red-100 text-red-700 border-red-300",
};

const STATUS_COLORS_ACTIVE: Record<string, string> = {
  "Ready for Development": "bg-gray-400 text-white border-gray-400",
  "Design Review":         "bg-gray-400 text-white border-gray-400",
  "In Progress":           "bg-blue-500 text-white border-blue-500",
  "Dev Complete":          "bg-indigo-500 text-white border-indigo-500",
  "Staging & QA Testing":  "bg-purple-500 text-white border-purple-500",
  "Staging testing":       "bg-purple-500 text-white border-purple-500",
  "Ready for Staging":     "bg-emerald-500 text-white border-emerald-500",
  "Ready for Production":  "bg-emerald-600 text-white border-emerald-600",
  "Recently Closed":       "bg-emerald-600 text-white border-emerald-600",
  "Closed":                "bg-emerald-600 text-white border-emerald-600",
  "Reopened":              "bg-red-500 text-white border-red-500",
};

const Toolbar: React.FC = () => {
  const [versions, setVersions] = useState<ReleaseVersion[]>([]);
  const [selected, setSelected] = useState<string>("");
  const { instance } = useMsal();
  const { accounts } = useMsal();
  const account = useAccount(accounts[0] || {});

  const [error, setError] = useState<string | null>(null);
  const [issues, setIssues] = useState<ReleaseVersionIssue[]>([]);
  const [summary, setSummary] = useState<FixVersionSummaryByCategory | null>(null);
  const [selectedIssue, setSelectedIssue] = useState<ReleaseVersionIssue | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isLoadingIssues, setIsLoadingIssues] = useState(false);
  const [projectKey, setProjectKey] = useState<string>("HRI2");
  const [lastSearchedVersion, setLastSearchedVersion] = useState<ReleaseVersion | null>(null);
  const [isPreviewPlanOpen, setIsPreviewPlanOpen] = useState(false);
  const [hasOpenedPreview, setHasOpenedPreview] = useState(false);
  const [activeStatuses, setActiveStatuses] = useState<Set<string>>(new Set());

  useEffect(() => {
    const fetchVersions = async () => {
      const data = await GetReleaseVersions(instance, account, projectKey);
      setVersions(data);
      setSelected("");
      setIssues([]);
      setSummary(null);
      setLastSearchedVersion(null);
      setHasOpenedPreview(false);
      setActiveStatuses(new Set());
    };
    fetchVersions();
  }, [instance, account, projectKey]);

  const handleSearch = async () => {
    const selectedVersion = versions.find(v => v.name === selected);
    if (!selectedVersion) return;

    setIsLoadingIssues(true);
    setError(null);
    setHasOpenedPreview(false);
    setActiveStatuses(new Set());
    try {
      const data = await GetReleaseVersionIssues(instance, account, projectKey, selectedVersion.id);
      setIssues(data.issues);
      setSummary(data.summary);
      setLastSearchedVersion(selectedVersion);
    } catch (e) {
      setIssues([]);
      setSummary(null);
      setLastSearchedVersion(null);
      setError("Failed to load issues. Please try again.");
    } finally {
      setIsLoadingIssues(false);
    }
  };

  const handlePreviewPlan = () => {
    if (!lastSearchedVersion) return;
    setHasOpenedPreview(true);
    setIsPreviewPlanOpen(true);
  };

  const toggleStatus = (status: string) => {
    setActiveStatuses(prev => {
      const next = new Set(prev);
      next.has(status) ? next.delete(status) : next.add(status);
      return next;
    });
  };

  // Unique statuses present in results, preserving a sensible order
  const availableStatuses = useMemo(() => {
    const seen = new Set<string>();
    const ordered: string[] = [];
    issues.forEach(i => {
      if (i.status && !seen.has(i.status)) {
        seen.add(i.status);
        ordered.push(i.status);
      }
    });
    return ordered;
  }, [issues]);

  const filteredIssues = useMemo(() =>
    activeStatuses.size === 0
      ? issues
      : issues.filter(i => i.status && activeStatuses.has(i.status)),
    [issues, activeStatuses]
  );

  return (
    <>
      <div className="rounded-lg bg-white shadow-md p-6 border border-indigo-100">
        {/* Search row */}
        <div className="flex items-center gap-2">

          {/* Project key */}
          <select
            className="border border-indigo-200 rounded-md px-2 py-2 text-sm focus:ring-indigo-400 focus:border-indigo-400 bg-white"
            value={projectKey}
            onChange={e => setProjectKey(e.target.value)}
          >
            <option value="HRI2">HRI2</option>
            <option value="PEN">PEN</option>
          </select>

          {/* Release version dropdown */}
          <Listbox as="div" className="relative" value={selected} onChange={setSelected}>
            <Listbox.Button className="relative min-w-56 cursor-pointer rounded-md bg-white border border-indigo-200 py-2 pl-3 pr-10 text-left shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 text-sm">
              <span className="block truncate text-gray-700">
                {selected || "Select a release version"}
              </span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-indigo-400">
                <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </span>
            </Listbox.Button>
            <Listbox.Options className="absolute z-50 mt-1 max-h-60 min-w-full w-max overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
              {versions.map((ver) => (
                <Listbox.Option
                  key={ver.id}
                  value={ver.name}
                  className={({ active }) =>
                    `cursor-pointer select-none relative py-2 pl-10 pr-4 ${
                      active ? "bg-indigo-50 text-indigo-900" : "text-gray-900"
                    }`
                  }
                >
                  {({ selected: isSelected }) => (
                    <>
                      <span className={`block truncate ${isSelected ? "font-medium" : "font-normal"}`}>
                        {ver.name}
                      </span>
                      {isSelected && (
                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-600">
                          <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </span>
                      )}
                    </>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Listbox>

          {/* Search */}
          <button
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-all text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleSearch}
            disabled={!selected}
          >
            Search
          </button>

          {/* Preview Plan — appears inline once a search has been run */}
          {lastSearchedVersion && (
            <>
              <div className="h-6 w-px bg-gray-200 mx-1" />
              <button
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-all text-sm font-medium flex items-center gap-2"
                onClick={handlePreviewPlan}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                  <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                </svg>
                Preview plan
              </button>
            </>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="mt-4 bg-red-50 text-red-600 p-2 rounded-md text-sm border border-red-200 flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            {error}
          </div>
        )}

        {/* Results */}
        <div className="mt-6">
          {isLoadingIssues ? (
            <div className="text-center text-gray-500 py-8">Loading issues...</div>

          ) : !lastSearchedVersion ? (
            /* Guiding empty state — shown before any search is run */
            <div className="py-8 px-4">
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-6">How it works</p>
              <div className="flex items-start gap-6">
                <div className="flex flex-col items-center gap-2 flex-1">
                  <div className="w-9 h-9 rounded-full bg-indigo-50 border border-indigo-200 flex items-center justify-center text-indigo-600 font-semibold text-sm flex-shrink-0">1</div>
                  <p className="text-sm font-medium text-gray-700 text-center">Pick a board &amp; version</p>
                  <p className="text-xs text-gray-400 text-center leading-relaxed">Select a Jira project key and the fix version you want to release</p>
                </div>
                <div className="pt-4 text-gray-300 text-xl font-light flex-shrink-0">→</div>
                <div className="flex flex-col items-center gap-2 flex-1">
                  <div className="w-9 h-9 rounded-full bg-indigo-50 border border-indigo-200 flex items-center justify-center text-indigo-600 font-semibold text-sm flex-shrink-0">2</div>
                  <p className="text-sm font-medium text-gray-700 text-center">Review the tickets</p>
                  <p className="text-xs text-gray-400 text-center leading-relaxed">Search to see all Jira issues in that fix version and their current status</p>
                </div>
                <div className="pt-4 text-gray-300 text-xl font-light flex-shrink-0">→</div>
                <div className="flex flex-col items-center gap-2 flex-1">
                  <div className="w-9 h-9 rounded-full bg-indigo-50 border border-indigo-200 flex items-center justify-center text-indigo-600 font-semibold text-sm flex-shrink-0">3</div>
                  <p className="text-sm font-medium text-gray-700 text-center">Preview the promotion plan</p>
                  <p className="text-xs text-gray-400 text-center leading-relaxed">See which repos and commits are going out, split into buckets by ticket coverage</p>
                </div>
                <div className="pt-4 text-gray-300 text-xl font-light flex-shrink-0">→</div>
                <div className="flex flex-col items-center gap-2 flex-1">
                  <div className="w-9 h-9 rounded-full bg-green-50 border border-green-200 flex items-center justify-center text-green-600 font-semibold text-sm flex-shrink-0">4</div>
                  <p className="text-sm font-medium text-gray-700 text-center">Promote</p>
                  <p className="text-xs text-gray-400 text-center leading-relaxed">Push the selected commits to staging or production</p>
                </div>
              </div>
            </div>

          ) : (
            <>
              {/* Next step hint */}
              {!hasOpenedPreview && (
                <div className="mb-4 flex items-center gap-3 px-4 py-3 bg-indigo-50 border border-indigo-200 rounded-lg text-sm text-indigo-700">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0 text-indigo-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <span>
                    Happy with the ticket list? Click <span className="font-semibold">Preview plan</span> above, select a target environment (staging or production), and review which repos and commits will be included before promoting.
                  </span>
                </div>
              )}

              {summary && <ReleaseVersionsSummaryBar summary={summary} />}

              {/* Status filter pills */}
              {availableStatuses.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap mb-3 mt-4">
                  <span className="text-xs text-gray-400 font-medium mr-1">Filter by status:</span>
                  {availableStatuses.map(status => {
                    const isActive = activeStatuses.has(status);
                    const count = issues.filter(i => i.status === status).length;
                    const colorClass = isActive
                      ? (STATUS_COLORS_ACTIVE[status] ?? "bg-indigo-500 text-white border-indigo-500")
                      : (STATUS_COLORS[status] ?? "bg-gray-100 text-gray-600 border-gray-300");
                    return (
                      <button
                        key={status}
                        onClick={() => toggleStatus(status)}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium transition-all ${colorClass}`}
                      >
                        {status}
                        <span className={`text-xs font-semibold ${isActive ? "opacity-80" : "opacity-60"}`}>
                          {count}
                        </span>
                      </button>
                    );
                  })}
                  {activeStatuses.size > 0 && (
                    <button
                      onClick={() => setActiveStatuses(new Set())}
                      className="text-xs text-gray-400 hover:text-gray-600 underline ml-1"
                    >
                      Clear
                    </button>
                  )}
                </div>
              )}

              <ReleaseVersionsTable
                issues={filteredIssues}
                onRowClick={issue => {
                  setSelectedIssue(issue);
                  setIsDetailsModalOpen(true);
                }}
              />

              {/* Filtered empty state */}
              {filteredIssues.length === 0 && activeStatuses.size > 0 && (
                <div className="text-center py-8 text-sm text-gray-400">
                  No tickets match the selected filters.
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <ReleaseVersionDetails
        issue={selectedIssue}
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
      />

      <PreviewPlanModal
        isOpen={isPreviewPlanOpen}
        onClose={() => setIsPreviewPlanOpen(false)}
        projectKey={projectKey}
        fixVersionId={lastSearchedVersion?.id ?? ""}
        fixVersionName={lastSearchedVersion?.name ?? ""}
      />
    </>
  );
};

export default Toolbar;